import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileText, ShieldCheck, Ghost, Braces, X,
  Download, Loader2, TerminalSquare, RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOG_LINES = [
  "> establishing secure enclave...",
  "> parsing document structure...",
  "> injecting invisible prompt directives...",
  "> weaving zero-width character noise...",
  "> substituting homoglyph tokens...",
  "> seeding decoy false information layer...",
  "> corrupting machine-readable metadata...",
  "> scrambling latent semantic signatures...",
  "> verifying human-readable layer intact...",
  "> sealing protected artifact...",
];

export const PoisonTool = () => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | processing | done
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const inputRef = useRef(null);
  const logTimer = useRef(null);
  const downloadingRef = useRef(false);

  useEffect(() => () => clearInterval(logTimer.current), []);

  const validate = (f) => {
    const n = f.name.toLowerCase();
    if (!n.endsWith(".pdf") && !n.endsWith(".epub")) {
      toast.error("Unsupported file", { description: "Only .pdf and .epub files are accepted." });
      return false;
    }
    if (f.size > 40 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum size is 40 MB." });
      return false;
    }
    return true;
  };

  const onFiles = (f) => { if (f && validate(f)) { setFile(f); setResult(null); setPhase("idle"); } };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    onFiles(e.dataTransfer.files?.[0]);
  }, []);

  // The download token is single-use: the server permanently deletes the shrouded
  // file the moment it is served. We fetch once (guarded) so the token isn't spent
  // twice, save via a blob, then lock the UI into the "downloaded & purged" state.
  const downloadFile = async (url, filename) => {
    if (downloadingRef.current || downloaded) return;
    downloadingRef.current = true;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("expired");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      setDownloaded(true);
      toast.success("Download complete", {
        description: "Your original and shrouded copies were deleted from our servers.",
      });
    } catch {
      toast.error("Download unavailable", {
        description: "This file was already downloaded and removed. Shroud it again to get a fresh copy.",
      });
    } finally {
      downloadingRef.current = false;
    }
  };

  const runLogs = () => {
    setLogs([]);
    let i = 0;
    logTimer.current = setInterval(() => {
      i += 1;
      setLogs((prev) => (i <= LOG_LINES.length ? [...prev, LOG_LINES[i - 1]] : prev));
    }, 550);
  };

  const poison = async () => {
    if (!file) return;
    setPhase("processing");
    runLogs();
    const started = Date.now();
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/poison`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const wait = Math.max(0, 3200 - (Date.now() - started));
      setTimeout(() => {
        clearInterval(logTimer.current);
        const url = `${API}/download/${data.download_token}`;
        setDownloadUrl(url);
        setResult(data);
        setDownloaded(false);
        setPhase("done");
        downloadFile(url, data.filename);
        toast.success("Shrouding complete", { description: "Your file is ready — use the download button." });
      }, wait);
    } catch (e) {
      clearInterval(logTimer.current);
      setPhase("idle");
      const msg = e.response?.data?.detail || "Something went wrong while processing your file.";
      toast.error("Shrouding failed", { description: msg });
    }
  };

  const reset = () => {
    setFile(null); setResult(null); setDownloadUrl(null); setDownloaded(false); setPhase("idle"); setLogs([]);
  };

  const kb = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(2)} MB` : `${(b / 1024).toFixed(1)} KB`);

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      {/* Left: dropzone / states */}
      <div className="lg:col-span-3">
        {phase === "idle" && (
          <div
            data-testid="upload-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`h-full min-h-[420px] flex flex-col items-center justify-center text-center px-8 py-16 cursor-pointer border border-dashed transition-colors duration-300 ${
              dragging ? "border-[#70250a] bg-[#70250a]/5" : "border-[#6d411c]/60 bg-[#041b24]/50 hover:border-[#70250a]"
            }`}
          >
            <input
              ref={inputRef} type="file" accept=".pdf,.epub" className="hidden"
              data-testid="file-input"
              onChange={(e) => onFiles(e.target.files?.[0])}
            />
            <Upload className="w-12 h-12 text-[#6d411c] mb-6" strokeWidth={1.2} />
            {file ? (
              <>
                <p className="font-cinzel text-xl text-[#E6E2D6] mb-2" data-testid="selected-file-name">{file.name}</p>
                <p className="font-mono text-xs text-[#485354] uppercase tracking-widest">{kb(file.size)} · ready to shroud</p>
                <button
                  data-testid="clear-file-btn"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#485354] hover:text-[#70250a] transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> remove
                </button>
              </>
            ) : (
              <>
                <p className="font-cinzel text-2xl text-[#E6E2D6] mb-3">Drop your manuscript</p>
                <p className="font-lora text-[#E6E2D6]/70 max-w-sm mb-5">
                  Drag a file here, or click to browse. Your book never leaves the secure enclave beyond processing.
                </p>
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] border border-[#485354]/50 px-4 py-2">
                  .pdf · .epub · max 40mb
                </span>
              </>
            )}
          </div>
        )}

        {phase === "processing" && (
          <div className="relative h-full min-h-[420px] overflow-hidden border border-[#70250a]/60 bg-[#020f14]" data-testid="processing-terminal">
            <img
              src="https://images.unsplash.com/photo-1765445773906-64a36ecfd20e"
              alt="" className="glitch-layer absolute inset-0 w-full h-full object-cover mix-blend-screen pointer-events-none"
            />
            <div className="relative z-10 p-8 font-mono text-sm">
              <div className="flex items-center gap-2 text-[#70250a] mb-6">
                <TerminalSquare className="w-4 h-4" />
                <span className="uppercase tracking-widest text-xs">shrouding in progress</span>
              </div>
              <div className="space-y-2">
                {logs.map((l, i) => (
                  <p key={i} className="text-[#E6E2D6]/90 rise">{l}</p>
                ))}
                <p className="text-[#70250a]">
                  <span className="cursor-blink">█</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {phase === "done" && result && (
          <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center px-8 py-16 border border-[#485354]/50 bg-[#041b24]/60" data-testid="result-panel">
            <ShieldCheck className="w-14 h-14 text-[#6d411c] mb-6" strokeWidth={1.3} />
            <p className="font-cinzel text-2xl text-[#E6E2D6] mb-2">Manuscript shrouded</p>
            <p className="font-mono text-xs text-[#485354] uppercase tracking-widest mb-3" data-testid="result-filename">{result.filename}</p>
            <p className="font-lora text-xs text-[#485354] mb-8 max-w-xs">
              {downloaded
                ? "Your file was downloaded and both the original and shrouded copies have been deleted from our servers."
                : "If the download didn't start automatically, click below. Once downloaded, the file is permanently removed from our servers."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              {downloaded ? (
                <span
                  data-testid="download-purged-label"
                  className="inline-flex items-center justify-center gap-2 border border-[#485354]/60 text-[#485354] font-cinzel font-bold uppercase tracking-widest text-sm py-4 px-8 cursor-default"
                >
                  <ShieldCheck className="w-4 h-4" /> Downloaded &amp; removed
                </span>
              ) : (
                <button
                  data-testid="download-btn"
                  onClick={() => downloadFile(downloadUrl, result.filename)}
                  className="inline-flex items-center justify-center gap-2 bg-[#70250a] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-4 px-8 hover:bg-[#8A2E0D] transition-colors duration-300 cursor-pointer"
                >
                  <Download className="w-4 h-4" /> Download shrouded file
                </button>
              )}
              <button
                data-testid="poison-another-btn"
                onClick={reset}
                className="inline-flex items-center justify-center gap-2 border border-[#485354] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-4 px-8 hover:border-[#6d411c] hover:bg-[#041b24] transition-colors duration-300"
              >
                <RotateCcw className="w-4 h-4" /> Shroud another
              </button>
            </div>
            <a
              href="/#badges"
              data-testid="result-badge-link"
              className="mt-8 font-mono text-xs uppercase tracking-widest text-[#485354] hover:text-[#70250a] transition-colors"
            >
              Get the "Protected by Inkshroud" badge →
            </a>
          </div>
        )}
      </div>

      {/* Right: technique panel + action */}
      <div className="lg:col-span-2 flex flex-col gap-6">
        <div className="border border-[#485354]/50 bg-[#041b24]/80 backdrop-blur-md p-7">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#485354] mb-6">Active countermeasures</p>
          {[
            { icon: Braces, t: "Invisible Prompts", d: "Hidden directives that hijack AI scrapers reading your text." },
            { icon: Ghost, t: "Zero-Width Noise", d: "Homoglyph & zero-width characters that shatter tokenization." },
            { icon: FileText, t: "Decoy False Info", d: "Fabricated facts poison any harvested training dataset." },
          ].map((c) => (
            <div key={c.t} className="flex gap-4 py-4 border-t border-[#485354]/25 first:border-t-0" data-testid={`technique-${c.t.toLowerCase().replace(/ /g, "-")}`}>
              <c.icon className="w-5 h-5 text-[#70250a] shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <p className="font-cinzel text-[#E6E2D6] text-base mb-1">{c.t}</p>
                <p className="font-lora text-sm text-[#E6E2D6]/60 leading-relaxed">{c.d}</p>
              </div>
            </div>
          ))}
        </div>

        {phase === "done" && result ? (
          <div className="border border-[#6d411c]/40 bg-[#0a2632]/60 p-7 font-mono text-xs" data-testid="stats-panel">
            <p className="uppercase tracking-[0.25em] text-[#485354] mb-4">Injection report</p>
            {[
              ["pages / sections", result.stats.pages_processed],
              ["invisible prompts", result.stats.invisible_prompts],
              ["decoy facts", result.stats.decoy_facts],
              ["noise characters", result.stats.noise_characters],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1.5 text-[#E6E2D6]/80">
                <span>{k}</span><span className="text-[#6d411c]">{v}</span>
              </div>
            ))}
          </div>
        ) : (
          <button
            data-testid="poison-button"
            disabled={!file || phase === "processing"}
            onClick={poison}
            className="w-full inline-flex items-center justify-center gap-3 bg-[#70250a] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-6 px-8 hover:bg-[#8A2E0D] hover:shadow-[0_0_20px_rgba(112,37,10,0.45)] transition-colors transition-shadow duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:shadow-none"
          >
            {phase === "processing" ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Shrouding…</>
            ) : (
              <><ShieldCheck className="w-5 h-5" /> Shroud this file</>
            )}
          </button>
        )}
        <p className="font-mono text-[10px] leading-relaxed text-[#485354]/70 uppercase tracking-wider">
          Processing happens on the server and the file is not stored. The human-readable content stays perfectly intact.
        </p>
      </div>
    </div>
  );
};
